// components/DashboardFilters.tsx
'use client';

import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Car, Filter, X } from 'lucide-react';

interface FilterParams {
  filter: 'all' | 'day' | 'week' | 'month' | 'year';
  date: string;
  municipality: string;
  brand: string;
  year: string;
}

interface DashboardFiltersProps {
  filters: FilterParams;
  onFilterChange: (filters: Partial<FilterParams>) => void;
  onReset: () => void;
}

// Mock data - replace with actual API calls
const MUNICIPALITIES = [
  'Tous',
  'Yaoundé I',
  'Yaoundé II',
  'Yaoundé III',
  'Yaoundé IV',
  'Yaoundé V',
  'Yaoundé VI',
  'Yaoundé VII',
];

const BRANDS = [
  'Toutes',
  'Toyota',
  'Nissan',
  'Hyundai',
  'Honda',
  'Mercedes',
  'Peugeot',
  'Renault',
  'Mitsubishi',
];

const YEARS = ['Toutes', '2024', '2023', '2022', '2021', '2020', '2019', '2018'];

export default function DashboardFilters({ filters, onFilterChange, onReset }: DashboardFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);

  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  const handleApplyFilters = () => {
    onFilterChange(tempFilters);
    setIsExpanded(false);
  };

  const handleClearFilters = () => {
    setTempFilters({
      filter: 'all',
      date: '',
      municipality: '',
      brand: '',
      year: '',
    });
    onReset();
    setIsExpanded(false);
  };

  const hasActiveFilters = filters.filter !== 'all' || 
    filters.date || 
    filters.municipality || 
    filters.brand || 
    filters.year;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      {/* Filter Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter size={20} className="text-slate-400" />
          <span className="font-semibold text-slate-700">Filtres</span>
          {hasActiveFilters && (
            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full font-medium">
              Actifs
            </span>
          )}
        </div>
        <button
        //   onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-primary hover:text-primary-dark font-medium transition"
        >
          {/* {isExpanded ? 'Réduire' : 'Développer'} */}
          {isExpanded ? 'Réduire' : ''}
        </button>
      </div>

      {/* Quick Filter Chips */}
      <div className="flex flex-wrap gap-2 mt-3">
        <button
          onClick={() => {
            const newFilters = { ...tempFilters, filter: 'all' as const, date: '' };
            setTempFilters(newFilters);
            onFilterChange(newFilters);
          }}
          className={`px-3 py-1 text-xs font-medium rounded-full transition ${
            filters.filter === 'all' 
              ? 'bg-primary text-white' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Tous
        </button>
        <button
          onClick={() => {
            const today = new Date().toISOString().split('T')[0];
            const newFilters = { ...tempFilters, filter: 'day' as const, date: today };
            setTempFilters(newFilters);
            onFilterChange(newFilters);
          }}
          className={`px-3 py-1 text-xs font-medium rounded-full transition ${
            filters.filter === 'day' 
              ? 'bg-primary text-white' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Aujourd'hui
        </button>
        <button
          onClick={() => {
            const now = new Date();
            const weekNumber = getWeekNumber(now);
            const newFilters = { 
              ...tempFilters, 
              filter: 'week' as const, 
              date: `${now.getFullYear()}-W${String(weekNumber).padStart(2, '0')}` 
            };
            setTempFilters(newFilters);
            onFilterChange(newFilters);
          }}
          className={`px-3 py-1 text-xs font-medium rounded-full transition ${
            filters.filter === 'week' 
              ? 'bg-primary text-white' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Cette semaine
        </button>
        <button
          onClick={() => {
            const now = new Date();
            const newFilters = { 
              ...tempFilters, 
              filter: 'month' as const, 
              date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` 
            };
            setTempFilters(newFilters);
            onFilterChange(newFilters);
          }}
          className={`px-3 py-1 text-xs font-medium rounded-full transition ${
            filters.filter === 'month' 
              ? 'bg-primary text-white' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Ce mois-ci
        </button>
        <button
          onClick={() => {
            const now = new Date();
            const newFilters = { 
              ...tempFilters, 
              filter: 'year' as const, 
              date: `${now.getFullYear()}` 
            };
            setTempFilters(newFilters);
            onFilterChange(newFilters);
          }}
          className={`px-3 py-1 text-xs font-medium rounded-full transition ${
            filters.filter === 'year' 
              ? 'bg-primary text-white' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Cette année
        </button>
      </div>

      {/* Expanded Filter Options */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Picker */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                <Calendar size={14} className="inline mr-1" />
                Date
              </label>
              <input
                type="date"
                value={tempFilters.date || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
              />
            </div>

            {/* Municipality Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                <MapPin size={14} className="inline mr-1" />
                Municipalité
              </label>
              <select
                value={tempFilters.municipality || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, municipality: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition bg-white"
              >
                <option value="">Toutes</option>
                {MUNICIPALITIES.filter(m => m !== 'Tous').map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Brand Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                <Car size={14} className="inline mr-1" />
                Marque
              </label>
              <select
                value={tempFilters.brand || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, brand: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition bg-white"
              >
                <option value="">Toutes</option>
                {BRANDS.filter(b => b !== 'Toutes').map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>

            {/* Year Filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                <Calendar size={14} className="inline mr-1" />
                Année
              </label>
              <select
                value={tempFilters.year || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, year: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition bg-white"
              >
                <option value="">Toutes</option>
                {YEARS.filter(y => y !== 'Toutes').map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition shadow-sm"
            >
              Appliquer les filtres
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition flex items-center gap-1"
            >
              <X size={16} />
              Effacer tout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for week number
function getWeekNumber(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}